import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts'

const SkillsSection = () => {
  const skillCategories = [
    {
      title: 'Languages & Frameworks',
      skills: [
        { name: 'PHP', level: 95, category: 'Backend' },
        { name: 'JavaScript', level: 90, category: 'Frontend' },
        { name: 'Node.js', level: 85, category: 'Backend' },
        { name: 'Laravel', level: 90, category: 'Backend' },
        { name: 'React', level: 85, category: 'Frontend' },
        { name: 'Tailwind CSS', level: 80, category: 'Frontend' },
        { name: 'D3.js', level: 75, category: 'Frontend' },
        { name: 'jQuery', level: 70, category: 'Frontend' },
      ]
    },
    {
      title: 'Databases',
      skills: [
        { name: 'MySQL', level: 90, category: 'Database' },
        { name: 'MongoDB', level: 70, category: 'Database' },
        { name: 'PostgreSQL', level: 75, category: 'Database' },
      ]
    },
    {
      title: 'System Design & Architecture',
      skills: [
        { name: 'Monolithic', level: 85, category: 'Architecture' },
        { name: 'Microservices', level: 80, category: 'Architecture' },
        { name: 'Design Patterns', level: 85, category: 'Architecture' },
        { name: 'SOLID Principles', level: 90, category: 'Architecture' },
        { name: 'RESTful API Design', level: 90, category: 'Architecture' },
        { name: 'GraphQL', level: 75, category: 'Architecture' },

      ]
    },
    {
      title: 'DevOps & Deployment',
      skills: [
        { name: 'Docker', level: 85, category: 'DevOps' },
        { name: 'GitHub Actions', level: 80, category: 'DevOps' },
        { name: 'AWS (EC2, S3)', level: 75, category: 'Cloud' },
        { name: 'Heroku', level: 70, category: 'Cloud' },
        { name: 'NGINX', level: 75, category: 'DevOps' },
        { name: 'Apache', level: 70, category: 'DevOps' },
        
      ]
    },
    {
      title: 'Testing & QA',
      skills: [
        { name: 'Selenium', level: 75, category: 'Testing' },
        { name: 'PHPUnit', level: 70, category: 'Testing' },
      ]
    },
    {
      title: 'Version Control & Project Management',
      skills: [
        { name: 'Git', level: 95, category: 'Version Control' },
        { name: 'JIRA', level: 85, category: 'Project Management' },
        { name: 'Bitbucket', level: 80, category: 'Version Control' },
        { name: 'Trello', level: 75, category: 'Project Management' },
      ]
    },
    {
      title: 'Artificial Intelligence & Machine Learning',
      skills: [
        { name: 'Python', level: 70, category: 'AI/ML' },
        { name: 'Agents & LLMs', level: 65, category: 'AI/ML' },
        { name: 'Automation workflows', level: 75, category: 'AI/ML' },
        { name: 'RAG Systems', level: 60, category: 'AI/ML' },
        { name: 'Vector Databases', level: 60, category: 'AI/ML' },

        
      ]
    }
  ]

  // Calculate average scores for radar chart
  const radarData = useMemo(() => {
    const categoryScores: { [key: string]: number[] } = {}
    
    skillCategories.forEach(category => {
      category.skills.forEach(skill => {
        if (!categoryScores[skill.category]) {
          categoryScores[skill.category] = []
        }
        categoryScores[skill.category].push(skill.level)
      })
    })
    
    return Object.entries(categoryScores).map(([category, scores]) => ({
      subject: category,
      fullMark: 100,
      average: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
    }))
  }, [skillCategories])

  const getSkillColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Backend': 'bg-blue-500',
      'Frontend': 'bg-purple-500',
      'Database': 'bg-green-500',
      'Architecture': 'bg-yellow-500',
      'DevOps': 'bg-red-500',
      'Cloud': 'bg-indigo-500',
      'Testing': 'bg-pink-500',
      'Version Control': 'bg-teal-500',
      'Project Management': 'bg-orange-500',
    }
    return colors[category] || 'bg-gray-500'
  }

  const getExpertiseBadge = (level: number) => {
    if (level >= 90) {
      return { label: 'Expert', icon: '🏆', color: 'from-yellow-400 to-yellow-600', bg: 'bg-yellow-500/20', text: 'text-yellow-400' }
    } else if (level >= 80) {
      return { label: 'Advanced', icon: '⭐', color: 'from-blue-400 to-blue-600', bg: 'bg-blue-500/20', text: 'text-blue-400' }
    } else if (level >= 70) {
      return { label: 'Proficient', icon: '✓', color: 'from-green-400 to-green-600', bg: 'bg-green-500/20', text: 'text-green-400' }
    } else {
      return { label: 'Intermediate', icon: '○', color: 'from-gray-400 to-gray-600', bg: 'bg-gray-500/20', text: 'text-gray-400' }
    }
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="glass-light rounded-lg p-4 shadow-xl">
          <p className="text-white font-semibold">{data.subject}</p>
          <p className="text-blue-400 text-sm">Proficiency: <span className="font-bold">{data.average}%</span></p>
        </div>
      )
    }
    return null
  }

  return (
    <section id="skills" className="py-20 relative">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Skills & Expertise
            </h2>
            <p className="text-gray-400 text-lg mb-4">Visual representation of my technical capabilities</p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full" />
          </motion.div>

          {/* Radar Chart */}
          <motion.div
            className="glass-light rounded-2xl p-8 mb-12"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-semibold mb-6 text-white text-center">Skill Category Overview</h3>
            <div className="w-full h-[400px] md:h-[500px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="rgba(255, 255, 255, 0.1)" />
                  <PolarAngleAxis 
                    dataKey="subject" 
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 100]}
                    tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  />
                  <Radar
                    name="Proficiency Level"
                    dataKey="average"
                    stroke="rgba(59, 130, 246, 0.8)"
                    fill="rgba(59, 130, 246, 0.3)"
                    fillOpacity={0.6}
                    strokeWidth={2}
                    animationDuration={1500}
                  />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Detailed Skills Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {skillCategories.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                className="glass-light rounded-2xl p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-semibold mb-6 text-white">{category.title}</h3>
                <div className="space-y-3">
                  {category.skills.map((skill, skillIndex) => {
                    const badge = getExpertiseBadge(skill.level)
                    return (
                      <motion.div
                        key={skillIndex}
                        className="group"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 + skillIndex * 0.05 }}
                        viewport={{ once: true }}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-300 font-medium group-hover:text-blue-400 transition-colors">
                            {skill.name}
                          </span>
                          <motion.div
                            className={`px-2 py-1 rounded text-xs font-semibold ${badge.bg} ${badge.text}`}
                            whileHover={{ scale: 1.1 }}
                            transition={{ duration: 0.2 }}
                          >
                            <span className="mr-1">{badge.icon}</span>
                            {badge.label}
                          </motion.div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-400">{skill.level}% Proficiency</span>
                          <div className="flex-1 h-1.5 bg-gray-700/50 rounded-full overflow-hidden">
                            <motion.div
                              className={`h-full rounded-full bg-gradient-to-r ${badge.color}`}
                              initial={{ width: 0 }}
                              whileInView={{ width: `${skill.level}%` }}
                              transition={{ duration: 0.8, delay: 0.5 + skillIndex * 0.05 }}
                              viewport={{ once: true }}
                            />
                          </div>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Soft skills section */}
          <motion.div
            className="glass-light rounded-2xl p-8 mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-semibold mb-6 text-white">Soft Skills</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {['Team Leadership', 'Agile/Scrum', 'Client Communication', 'Cross-Functional Collaboration'].map((skill, index) => (
                <motion.div
                  key={index}
                  className="bg-white/5 rounded-lg p-4 text-center hover:bg-white/10 transition-all duration-300 cursor-default"
                  whileHover={{ scale: 1.05, y: -5 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.7 + index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="text-blue-400 mb-2">
                    <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-300 font-medium">{skill}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default SkillsSection